/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Key,
  type ReactNode,
} from "react";
import { HiddenSelect, useButton, useFocusRing, useSelect } from "react-aria";
import {
  Item,
  useSelectState as useAriaSelectState,
  type SelectProps,
} from "react-stately";
import { cssFns } from "@superweb/css";
import { useUiColors } from "../theme";
import { useTypo } from "../typo";
import {
  ClearButton,
  Field,
  FieldDescription,
  FieldErrorMessage,
  FieldLabel,
} from "../fields";
import { icons } from "../icons";
import { ListBox, ListBoxPopover, OptionContent } from "../listbox";
import { useMessage } from "#intl";
import { useIsMobile } from "../mobile-context";
import { useDebouncedState } from "../state/state";
import { useSubmitGuard } from "../experimental-form";

type InvalidRules = {
  required: boolean;
};

export type SelectState<Value> = {
  /**
   * Сurrent value of select, that is key of the selected item
   */
  value?: Value;

  /**
   * When set `false` the error message is not visible even if is set.
   * Is set on change depending on the interaction.
   * Can be set externally to force hide/show the error message.
   * The field has invalid state when both the `errorMessage` is not empty and `errorVisible` is `true`.
   */
  errorVisible?: boolean;

  /**
   * The error message associated with the field.
   * Visible only when `errorVisible` is `true`.
   * The field has invalid state when both the `errorMessage` is not empty and `errorVisible` is `true`.
   */
  errorMessage?: string;

  /**
   * Is set to `true` on change if the field contains invalid value.
   */
  invalid?: boolean;
};

export const Select = <Value extends string>({
  label,
  description,
  ariaDescribedBy,
  required = false,
  state,
  placeholder,
  disabled = false,
  options,
  icon,
  onChange,
  onOpenChange,
  validate,
}: {
  /**
   * Text for field's label, that describes field's meaning.
   * No need to specify input examples in label's text.
   * The label is used for the accessibility of the element
   *
   * Links:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
   * https://www.w3.org/WAI/tutorials/forms/labels/
   */
  label: string;

  /**
   * Text for field's description, that describes in detail the purpose of this field.
   *
   * Links:
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-description
   */
  description?: string;

  /**
   * Text for field's description, that describes in detail the purpose of this field.
   * Use `ariaDescribedBy` when you need to use an element with text and additional content
   * (e.g., icons, images, or formatting) to describe the field.
   * This allows for associating the field with rich, external descriptions that provide additional context.
   *
   * Links:
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-describedby
   */
  ariaDescribedBy?: string;

  /**
   * If `true`, the input element is required.
   * If `true`, the clear button element isn't rendered.
   * @defaultValue false
   */
  required?: boolean;

  /**
   * Current field's state.
   * The state stores the interactive fields, that change when interacting with the component.
   */
  state: SelectState<Value>;

  /**
   * Temporary text that displays in field when it is empty.
   */
  placeholder?: string;

  /**
   * If `true`, the component is disabled.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * Select's options, that make up the drop-down list.
   */
  options: {
    /**
     * Will be passed to `onChange` as `state.value`
     * when user selects the option.
     */
    value: Value;
    /**
     * Describes the option's meaning.
     */
    label: string;
    /**
     * Additional text displayed below the main text in the option.
     */
    description?: string;
    /**
     * Select option icon.
     * maximum: 40px
     */
    icon?: ReactNode;
    /**
     * Disable option
     */
    disabled?: boolean;
  }[];

  /**
   * Icon at the start of the field.
   */
  icon?: ReactNode;

  /**
   * Callback fired when the state is changed.
   * @param state - Recommended state for the component after the change.
   */
  onChange: (
    state: SelectState<Value>,
    info: {
      invalid: {
        /**
         * If `true`, the input element is required.
         */
        required: boolean;
      };
    },
  ) => void;

  /**
   * Callback fired when dropdown is shown/hidden.
   * @param isOpen - Current state of the dropdown.
   */
  onOpenChange?: (isOpen: boolean) => void;

  /**
   * Custom validation function.
   * @param state - Current state of the field.
   * @returns Error message if validation fails, undefined otherwise.
   */
  validate?: (state: SelectState<Value>) => string | undefined;
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();

  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);

  const currentState = useRef<SelectState<Value>>(state);

  const getErrorMessage = useErrorMessage();

  const { isFocused, focusProps } = useFocusRing({
    within: true,
  });

  const valueToKey = (value: Value) => `_${value}`;
  const keyToValue = (key: Key) =>
    typeof key === "string" && key.startsWith("_")
      ? (key.slice(1) as Value)
      : undefined;

  const items = options.map(({ value, label, description, icon }) => (
    <Item key={valueToKey(value)} textValue={label}>
      <OptionContent label={label} description={description} icon={icon} />
    </Item>
  ));

  const handleValidate = useCallback(
    (currentState: SelectState<Value>) => {
      const invalidRules = validateSelect({
        value: currentState.value,
        required,
      });
      const customErrorMessage = validate?.(currentState);
      const errorMessage =
        customErrorMessage ||
        (Object.values(invalidRules).some(Boolean)
          ? getErrorMessage(invalidRules)
          : undefined);

      return {
        invalidRules: {
          ...invalidRules,
          custom: Boolean(customErrorMessage),
        },
        errorMessage,
      };
    },
    [validate, required, getErrorMessage],
  );

  const handleChange = useCallback(
    (currentState: SelectState<Value>) => {
      const { invalidRules, errorMessage } = handleValidate(currentState);

      onChange(
        {
          ...currentState,
          errorMessage,
          errorVisible: currentState.errorVisible,
          invalid: Object.values(invalidRules).some(Boolean),
        },
        {
          invalid: { required: invalidRules.required },
        },
      );
    },
    [onChange, handleValidate],
  );

  useSubmitGuard({
    validate: () => {
      const { invalidRules } = handleValidate(state);
      return !Object.values(invalidRules).some(Boolean);
    },
    onInvalid: () =>
      handleChange({
        ...state,
        errorVisible: true,
      }),
  });

  const ariaProps: SelectProps<String> = {
    label,
    description,
    errorMessage: state.errorMessage,
    selectedKey: state.value !== undefined ? valueToKey(state.value) : null,

    validationState:
      state.errorVisible && state.errorMessage ? "invalid" : "valid",

    isDisabled: disabled,

    disabledKeys: options.flatMap(({ disabled, value }) =>
      disabled ? [valueToKey(value)] : [],
    ),

    isRequired: required,

    children: items,
    onSelectionChange: (key: Key | null) => {
      if (key === null) {
        return;
      }

      const newState = {
        ...state,
        value: keyToValue(key),
        errorVisible: false,
        errorMessage: undefined,
      };

      handleChange(newState);
      currentState.current = newState;
    },

    onOpenChange: (isOpen) => {
      if (!isOpen) {
        handleChange({
          ...currentState.current,
          errorVisible: true,
        });
      }
      onOpenChange?.(isOpen);
    },

    onBlur: () => {
      const value = currentState.current.value;
      if (value !== undefined && typeof value !== "string") {
        throw Error("value is not a string");
      }
      handleChange({
        ...currentState.current,
        value,
        errorVisible: true,
        errorMessage: undefined,
      });
    },
  };

  const ariaState = useAriaSelectState(ariaProps);
  const selectedItem = ariaState.selectedItem as
    | typeof ariaState.selectedItem
    | undefined;

  const {
    labelProps,
    descriptionProps,
    errorMessageProps,
    triggerProps,
    valueProps,
    menuProps,
  } = useSelect(
    { ...ariaProps, "aria-describedby": ariaDescribedBy },
    ariaState,
    buttonRef,
  );

  const isMobile = useIsMobile();

  // Workaround for https://github.com/adobe/react-spectrum/issues/1513
  // Сorrects the triggering of clicks on elements under the overlay
  // by debouncing the open state
  const debouncedIsOpen = useDebouncedState(ariaState.isOpen, 0);
  // apply debounced state only on mobile in `true` -> `false` phase
  const isOpen = (isMobile && debouncedIsOpen) || ariaState.isOpen;

  const { buttonProps } = useButton(triggerProps, buttonRef);

  const isShrunk = isFocused || isOpen || state.value !== undefined;

  useEffect(() => {
    const onKeyup = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleChange({
          ...state,
          errorVisible: true,
        });
      }
    };

    window.addEventListener("keyup", onKeyup);
    return () => {
      window.removeEventListener("keyup", onKeyup);
    };
  }, [state, handleChange]);

  useEffect(() => {
    currentState.current = state;
  }, [state]);

  const onClearButtonPress = () => {
    handleChange({
      ...state,
      value: undefined,
      errorVisible: false,
    });
    buttonRef.current?.focus();
  };

  return (
    <>
      <Field
        fieldProps={focusProps}
        fieldRef={ref}
        shrunk={isShrunk}
        focused={isFocused || isOpen}
        disabled={disabled}
        icon={icon}
        label={
          <FieldLabel shrunk={isShrunk} labelProps={labelProps}>
            {label}
          </FieldLabel>
        }
        input={
          <>
            <HiddenSelect
              label={label}
              state={ariaState}
              triggerRef={buttonRef}
            />

            <button
              {...buttonProps}
              ref={buttonRef}
              css={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                ...cssFns.margin("0"),
                ...cssFns.border({ width: "0" }),
                ...cssFns.padding("0"),
                backgroundColor: "transparent",
                outlineStyle: "none",
              }}
            >
              <div
                css={{
                  display: "grid",
                }}
              >
                <span
                  {...valueProps}
                  css={{
                    ...typo({
                      level: "body2",
                      density: "normal",
                      weight: "regular",
                    }),
                    color:
                      state.value !== undefined
                        ? uiColors.text
                        : uiColors.textMinor,
                    overflowX: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {selectedItem
                    ? ariaState.selectedItem?.props.children.props.label
                    : placeholder}
                </span>
              </div>
            </button>
          </>
        }
        {...(!required && {
          clearButton: (
            <ClearButton
              visible={state.value !== undefined && (isFocused || isOpen)}
              onPress={onClearButtonPress}
            />
          ),
        })}
        toolbar={
          <div
            css={{
              width: "32px",
              height: "32px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: uiColors.text,
              cursor: "pointer",
            }}
          >
            <icons.ChevronDownM aria-hidden="true" />
          </div>
        }
        descriptionAndError={
          state.errorVisible && state.errorMessage ? (
            <FieldErrorMessage errorMessageProps={errorMessageProps}>
              {state.errorMessage}
            </FieldErrorMessage>
          ) : (
            description && (
              <FieldDescription descriptionProps={descriptionProps}>
                {description}
              </FieldDescription>
            )
          )
        }
        onClick={() => {
          if (disabled) return;

          if (!isOpen) {
            buttonRef.current?.click();
          }
        }}
      />

      {isOpen && Boolean(ariaState.collection.size) && (
        <ListBoxPopover
          popoverRef={popoverRef}
          triggerRef={ref}
          state={ariaState}
          maxHeight={350}
        >
          <ListBox
            ariaListBoxProps={menuProps}
            listBoxRef={listBoxRef}
            state={ariaState}
          />
        </ListBoxPopover>
      )}
    </>
  );
};

export const createSelectState = <Value extends string>(
  defaultValue?: SelectState<Value>,
): SelectState<Value> => {
  return {
    value: undefined,
    errorVisible: false,
    errorMessage: undefined,
    ...defaultValue,
  };
};

export const useSelectState = <Value extends string>(
  defaultValue?: SelectState<Value>,
) => {
  return useState<SelectState<Value>>(createSelectState(defaultValue));
};

const useErrorMessage = () => {
  const message = useMessage();

  return (invalidRules: InvalidRules) => {
    if (invalidRules.required) {
      return message({
        id: "8d620bd6-eeec-4b94-bb6f-29bb6fc9c9c9",
        context:
          "Select field. Error message in platform libraries in the Select field component.",
        default: "Required field",
      });
    }

    return undefined;
  };
};

const validateSelect = ({
  value,
  required,
}: {
  value: any;
  required?: boolean;
}) => {
  const invalid = {
    required: Boolean(required && !value),
  };

  return invalid;
};
